import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
// ⚠️ `signOut` is imported ALIASED, because the provider declares its own
// `const signOut` (the context method) further down, which shadows a bare
// import throughout the component body.
//
// ⚠️ Round-2 B3 corrected why this matters, and the first version of this
// comment was WRONG — recorded because the wrong version is the more
// comforting one. It claimed the unaliased `signOut(auth)` "did nothing"
// because `auth.signOut()` threw. That is true only under
// `tests/unit/auth/AuthContext.login.test.tsx`, whose mocked `auth` exposes
// `onAuthStateChanged` and a `currentUser` getter and no `signOut`. I read the
// test's failure and generalised it to production.
//
// In production the shadowed call reaches the context method below, whose body
// is a REAL `auth.signOut()` — and then `toast.success("Logged out
// successfully")` plus an auth-state reset. So the pre-alias behaviour was not
// "nothing": it was announcing a successful LOGOUT to someone whose LOGIN had
// just failed a check they never asked about. That is the §6a-bis
// calm-wrong-answer, and it is the actual argument for the alias.
import { User, UserCredential, signInWithEmailAndPassword, signOut as firebaseSignOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth, getUserProfile } from '../services/firebaseService';
import { ensureUserIdentityBaseline, IdentityBaselineSource } from '@/services/userIdentityBaselineService';
import { AuthenticationState } from '@/utils/tokenUtils';
import { handleFirebaseError } from '@/utils/errorUtils';
import {
  boundAuthCall,
  isAuthTransportTimeout,
  authFailureAttestationMessage,
  AuthAttestationRejectedError,
  AUTH_ATTESTATION_REJECTED_MESSAGE,
  SENTRY_CAPTURED_LOGIN_CODES,
} from '@/lib/auth-transport';
import {
  APP_CHECK_TOKEN_INVALID_CODE,
  readAppCheckEvidence,
  readRefusalEvidence,
} from '@/lib/appCheckOutcome';
import { persistLoginUserRecord } from '@/lib/loginUserRecord';
import { enforceProAccessGate } from '@/lib/proAccessGate';
import { mintApexSession } from '@/lib/apexSessionMint';
import { revokeApexSession, type ApexRevokeOutcome } from '@/lib/apexSessionRevoke';
import { attemptApexSsoBootstrap } from '@/lib/apexSsoBootstrap';
import {
  clearApexSsoSuppression,
  isApexSsoBootstrapSuppressed,
  suppressApexSsoBootstrap,
} from '@/lib/apexSsoSuppression';
import { clearApexSessionObserved } from '@/lib/apexObservedState';
import { shouldEndProSessionForApex, shouldRunPresenceCheck } from '@/lib/apexSessionChecker';
import { hadPersistedAuthUserAtBoot } from '@/lib/bootAuthSnapshot';
import { applySentryRoleTag, resolveSentryRoleTag } from '@/lib/analytics/sentryRoleTag';
import { toast } from "@/components/ui/sonner";
import WebsiteLogo from "@/components/common/WebsiteLogo";

// KB-2606: the "Loading your session…" gate must always resolve. Total
// budget before we release the gate unconditionally, even if the auth
// listener never fires (H3 — corrupt/evicted IndexedDB persistence).
// Exported so tests reference the real value instead of a duplicated magic
// number.
//
// KB-3425: a gate released BY this budget, rather than by an auth answer, also
// turns on the /logout rescue (KB-1670), rendered on whatever screen the
// release lands on. The rescue used to live on the gate itself, behind a
// 10000 ms timer that only fired while the gate was still up — and this
// budget had always released the gate at 8000 ms, so the link rendered on no
// session at all.
export const BOOTSTRAP_BUDGET_MS = 8000;

// KB-3271 AC-4: the message shown when the reverse checker signs a director out
// because their apex (kidsbooked.com) session is gone. This is NOT a user-clicked
// logout, so it must explain itself rather than read as "Logged out successfully".
// Exported so the source and any assertion share one string (no drift).
//
// KB-3593: what is measured when this fires is "no apex session AND a forced
// refresh was refused as invalidated" — a revoke, a disabled account, or a deleted
// one (the SDK maps USER_NOT_FOUND to user-token-expired). A kidsbooked.com
// sign-out produces the revoke, but it is not the only cause, so the copy names it
// as the example, not the cause.
export const APEX_ENDED_SIGN_OUT_MESSAGE =
  "You've been signed out of KidsBooked Pro because your KidsBooked sign-in was ended elsewhere, for example by signing out on kidsbooked.com.";

// KB-3593 AC-2 (A): shown when this load started with a persisted session and the
// SDK's boot reload dropped it. States what happened and no cause — the SDK clears
// the user on a revoke, a disabled account, an App Check refusal and more, and
// which one it was is not observable here.
export const SESSION_ENDED_NOTICE = "Your session ended — please sign in again.";

interface AuthContextProps {
  currentUser: User | null;
  loading: boolean;
  userRoles?: string[];
  // Adding the properties that are being used elsewhere
  isAdmin?: boolean;
  userProfile?: any;
  refreshAuthToken?: () => Promise<string | null>;
  login?: (email: string, password: string) => Promise<User>;
  // KB-3270: `federated` (default true) also clears the apex (kidsbooked.com)
  // session cookie, so a user-initiated logout ends the session on both origins.
  // Refusal/eject paths pass `{ federated: false }` — see the AC-4 disposition
  // table in AuthContext's sign-out spec.
  signOut?: (opts?: { federated?: boolean }) => Promise<void>;
  authenticationState: AuthenticationState;
}

const AuthContext = createContext<AuthContextProps>({
  currentUser: null,
  loading: true,
  authenticationState: {
    isAuthenticated: false,
    lastTokenRefresh: null,
    tokenExpirationTime: null,
    hasRoleClaim: false,
    tokenValid: false
  }
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEscape, setShowEscape] = useState(false);
  const [sessionEndedAtBoot, setSessionEndedAtBoot] = useState(false);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [authState, setAuthState] = useState<AuthenticationState>({
    isAuthenticated: false,
    lastTokenRefresh: null,
    tokenExpirationTime: null,
    hasRoleClaim: false,
    tokenValid: false
  });

  // KB-2606 code-review round 1: bumped on every onAuthStateChanged firing.
  // `enrichUserSession` captures the generation it was dispatched for and
  // checks it before every state write — a sign-out (or any newer auth
  // event) landing while a PREVIOUS session's enrichment is still in
  // flight must not have its reset state clobbered by that stale write.
  // Releasing the gate immediately (rather than keeping the app inert
  // until enrichment finishes, as the pre-fix code did) makes the app
  // fully interactive DURING that window, which is what makes the race
  // reachable in practice, not just in theory.
  const authGenerationRef = useRef(0);

  // KB-4771: Sentry's `role` tag follows the users-doc role. Both inputs are
  // reset by the sign-out branch of onAuthStateChanged, so logout clears it.
  const profileRole = userProfile?.role;
  useEffect(() => {
    applySentryRoleTag(resolveSentryRoleTag(currentUser !== null, profileRole));
  }, [currentUser, profileRole]);

  // KB-3228: guards the apex-SSO bootstrap to at most one attempt per mount.
  // See the onAuthStateChanged sign-out branch below for why re-attempting
  // would loop.
  const ssoBootstrapAttemptedRef = useRef(false);

  // KB-3994: whether the page's one attempt (above) may actually fire. The door
  // is for a load that ARRIVES signed out, so any signed-in answer closes it: on
  // a page that has been signed in, the first signed-out answer is a sign-out
  // (this tab's, or another tab's arriving through shared persistence), and it
  // spends the attempt without adopting. Only a `{ federated: false }` eject
  // re-opens it; see `signOut`.
  const ssoDoorOpenRef = useRef(true);

  // KB-3271: last time the reverse apex-presence check actually ran, for the AC-3
  // focus/visibility throttle. `null` until the first check.
  const lastApexCheckAtRef = useRef<number | null>(null);

  const navigate = useNavigate();

  // Check if user is admin based on UID (for the known admin user) or profile role
  const isAdmin = (() => {
    // Known admin user IDs
    const adminUIDs = [
      "L0LaPLuz8Vhi75oQ7SIxiaQr6ty2", // Original admin UID
      "12sBfdF9ZxUaIx6qxnoNsGcT5FI3"  // coby@kidsbooked.com UID
    ];
    
    if (currentUser?.uid && adminUIDs.includes(currentUser.uid)) {
      return true;
    }
    
    // Check profile role
    if (userProfile?.role === 'admin_user') {
      return true;
    }
    
    // Check token claims
    if (userRoles?.includes('admin_user')) {
      return true;
    }
    
    return false;
  })();

  // KB-2606: profile + custom-claims enrichment run OFF the blocking gate.
  // `setLoading(false)` already ran (see the onAuthStateChanged handler
  // below) by the time this is called — a hang or failure in here degrades
  // the header/role-gated UI, never the app itself (AC3).
  //
  // `generation` is the value `authGenerationRef` held when THIS call was
  // dispatched. Every state write below is guarded by `stale()` — if a
  // newer onAuthStateChanged event (e.g. a sign-out) has fired since this
  // call started, `authGenerationRef.current` has moved on, and this
  // (superseded) call must not overwrite the fresher state with stale
  // profile/role/auth data (code-review round 1, PR #1089).
  const enrichUserSession = async (user: User, generation: number) => {
    const stale = () => authGenerationRef.current !== generation;
    try {
      Sentry.addBreadcrumb({ category: 'auth-bootstrap', message: 'getIdTokenResult: start' });
      const tokenResult = await user.getIdTokenResult();
      Sentry.addBreadcrumb({ category: 'auth-bootstrap', message: 'getIdTokenResult: resolved' });
      const expirationDate = new Date(tokenResult.expirationTime);

      console.log("Token claims:", tokenResult.claims);
      console.log("Email verified:", user.emailVerified);

      // Shared identity baseline (see docs/user-creation-cf-info.md) — once per session per uid
      try {
        if (!user.isAnonymous && user.email && typeof sessionStorage !== "undefined") {
          const sessKey = `kb_identity_baseline_${user.uid}`;
          if (!sessionStorage.getItem(sessKey)) {
            Sentry.addBreadcrumb({ category: 'auth-bootstrap', message: 'ensureUserIdentityBaseline: start' });
            await ensureUserIdentityBaseline({ source: IdentityBaselineSource.AUTH_SESSION });
            sessionStorage.setItem(sessKey, "1");
            Sentry.addBreadcrumb({ category: 'auth-bootstrap', message: 'ensureUserIdentityBaseline: resolved' });
          }
        }
      } catch (baselineErr) {
        console.warn("ensureUserIdentityBaseline (auth session) failed:", baselineErr);
        Sentry.addBreadcrumb({ category: 'auth-bootstrap', level: 'warning', message: 'ensureUserIdentityBaseline: failed' });
      }

      // Try to fetch user profile, but don't fail if permissions are denied
      let profile: any = null;
      try {
        Sentry.addBreadcrumb({ category: 'auth-bootstrap', message: 'getUserProfile: start' });
        profile = await getUserProfile(user.uid);
        if (!stale()) {
          setUserProfile(profile);
          console.log("User profile loaded:", profile);
        }
        Sentry.addBreadcrumb({ category: 'auth-bootstrap', message: 'getUserProfile: resolved' });
      } catch (profileError) {
        console.warn("Could not fetch user profile, continuing with token claims only:", profileError);
        Sentry.addBreadcrumb({ category: 'auth-bootstrap', level: 'warning', message: 'getUserProfile: failed' });
        // For known admin users, create a minimal profile
        const adminUIDs = [
          "L0LaPLuz8Vhi75oQ7SIxiaQr6ty2", // Original admin UID
          "12sBfdF9ZxUaIx6qxnoNsGcT5FI3"  // coby@kidsbooked.com UID
        ];

        if (adminUIDs.includes(user.uid)) {
          profile = {
            uid: user.uid,
            email: user.email,
            role: 'admin_user'
          };
          if (!stale()) {
            setUserProfile(profile);
            console.log("Created fallback admin profile for known admin user");
          }
        }
      }

      // Ensure custom claims are present; sync if missing (skip for anonymous users)
      try {
        const hasRoleClaim = !!tokenResult.claims?.role;
        if (!hasRoleClaim && !user.isAnonymous) {
          Sentry.addBreadcrumb({ category: 'auth-bootstrap', message: 'syncMyClaims: start' });
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const { app } = await import('../services/firebaseService');
          const functions = getFunctions(app, 'us-east4');
          const syncMyClaims = httpsCallable(functions, 'syncMyClaims');
          const resp: any = await syncMyClaims({});
          if (resp?.data?.updated) {
            // Single forced refresh to pick up new claims
            await user.getIdToken(true);
          }
          Sentry.addBreadcrumb({ category: 'auth-bootstrap', message: 'syncMyClaims: resolved' });
        }
      } catch (e) {
        console.warn('Failed to sync custom claims on login:', e);
        Sentry.addBreadcrumb({ category: 'auth-bootstrap', level: 'warning', message: 'syncMyClaims: failed' });
      }

      // Set user roles based on token claims
      const roles = [];
      if (tokenResult.claims?.role) {
        roles.push(tokenResult.claims.role);
        console.log("Role from token claims:", tokenResult.claims.role);
      }

      // Add role from the freshly-fetched profile, if available
      if (profile?.role) {
        roles.push(profile.role);
      }

      // For known admin users, ensure admin role is set
      const adminUIDs = [
        "L0LaPLuz8Vhi75oQ7SIxiaQr6ty2", // Original admin UID
        "12sBfdF9ZxUaIx6qxnoNsGcT5FI3"  // coby@kidsbooked.com UID
      ];

      if (adminUIDs.includes(user.uid)) {
        if (!roles.includes('admin_user')) {
          roles.push('admin_user');
        }
      }

      if (!stale()) {
        setUserRoles(roles);

        setAuthState({
          isAuthenticated: true,
          lastTokenRefresh: new Date(),
          tokenExpirationTime: expirationDate,
          hasRoleClaim: !!tokenResult.claims?.role,
          tokenValid: expirationDate > new Date()
        });

        console.log("User roles set:", roles);
      }
    } catch (error) {
      console.error("Error getting token details:", error);
      Sentry.addBreadcrumb({ category: 'auth-bootstrap', level: 'error', message: 'enrichUserSession: failed' });
      if (!stale()) {
        setAuthState({
          isAuthenticated: true,
          lastTokenRefresh: new Date(),
          tokenExpirationTime: null,
          hasRoleClaim: false,
          tokenValid: false
        });
      }
    }
  };

  // KB-3271 AC-4: end the pro session because the apex (kidsbooked.com) session is
  // gone. The SAME local sign-out the Logout button gives, MINUS the apex revoke —
  // the apex session is already gone, which is the trigger, so there is nothing to
  // revoke. Not a bare error: an explanatory toast plus a clean landing on /login
  // (the router does not itself send a signed-out user there — a ProtectedRoute
  // renders a generic "something went wrong" page — so navigate explicitly).
  const endProSessionForApex = async (uid: string): Promise<void> => {
    // Owns the flag clear: this path calls auth.signOut() directly (to control
    // the toast), so the context signOut's clear never runs. Since KB-3593 the
    // SDK has usually signed the user out already, inside the forced refresh
    // that confirmed the revoke — the auth.signOut() below is then a no-op.
    clearApexSessionObserved(uid);
    try {
      await auth.signOut();
    } catch (signOutError) {
      console.warn('Apex-ended sign-out: auth.signOut() failed:', signOutError);
    }
    toast.success(APEX_ENDED_SIGN_OUT_MESSAGE);
    navigate('/login');
  };

  // KB-3271 AC-1/AC-2/AC-3: poll the apex endpoint and, ONLY on a definitive
  // "there is no apex session", end the pro session. Gated three ways:
  //  - no signed-in user → nothing to end;
  //  - AC-3 throttle → focus/visibility churn cannot hammer the endpoint (the load
  //    call runs first, with lastApexCheckAtRef null, so it always passes);
  //  - AC-1 + AC-2 (inside shouldEndProSessionForApex) → does nothing unless a live
  //    apex session was observed for this uid AND the endpoint definitively says it
  //    is gone AND (KB-3593) the pro credential was revoked; every non-definitive
  //    answer fails open.
  // Always called un-awaited by its triggers (AC-3 — never blocks first render).
  const runApexPresenceCheck = async (): Promise<void> => {
    const user = auth.currentUser;
    if (!user) return;
    const now = Date.now();
    if (!shouldRunPresenceCheck(lastApexCheckAtRef.current, now)) return;
    lastApexCheckAtRef.current = now;
    if (await shouldEndProSessionForApex(user)) {
      await endProSessionForApex(user.uid);
    }
  };

  // KB-3271 AC-3: re-check on tab focus / visibilitychange when the tab becomes
  // visible (throttled inside runApexPresenceCheck). Mount-once; the listener
  // reads auth state live at call time.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void runApexPresenceCheck();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log("Setting up auth state listener");
    let effectActive = true;
    let gateSettled = false;
    let unsubscribe: (() => void) | undefined;

    // KB-2606 AC2: the ONLY place `setLoading(false)` runs. Called either
    // when onAuthStateChanged fires for the first time, or when the total
    // budget below expires — whichever happens first. Idempotent, so it's
    // safe to call from both races without a double-render.
    const releaseGate = (reason: string) => {
      if (gateSettled) return;
      gateSettled = true;
      Sentry.addBreadcrumb({ category: 'auth-bootstrap', message: `gate released: ${reason}` });
      setLoading(false);
    };

    // AC2: total budget. Scheduled synchronously, independent of the listener
    // chain below, so a hang anywhere in that chain (onAuthStateChanged never
    // firing — H3 — or the apex-SSO attempt never settling) still resolves the
    // gate.
    const budgetTimer = setTimeout(() => {
      Sentry.addBreadcrumb({ category: 'auth-bootstrap', level: 'warning', message: 'bootstrap budget exceeded — releasing gate without a settled auth state' });
      // KB-3425: read BEFORE releaseGate flips it. Only a gate this budget
      // actually releases gets the rescue; one an auth answer already released
      // belongs to a session that loaded.
      if (!gateSettled) setShowEscape(true);
      releaseGate('budget-exceeded');
    }, BOOTSTRAP_BUDGET_MS);

    (async () => {
      // KB-2606 AC4: nothing async may precede listener registration here — a
      // fire-and-forget IIFE used to race `onAuthStateChanged`, and the fix was
      // to make the ordering explicit.
      //
      // KB-3425: this used to be a `setPersistence(browserLocal → session →
      // inMemory)` chain right here, and then — briefly, and wrongly — an
      // `await authPersistenceReady` for a bounded IndexedDB upgrade. Both are
      // gone, and the absence is the point: persistence is now settled entirely
      // at construction in `firebaseService.ts`, by a hierarchy with no
      // IndexedDB member at all.
      //
      // ⛔ Do not reintroduce an await here. `setPersistence` runs on
      // `AuthImpl`'s serial queue, so an unbounded one blocks every later auth
      // operation including `signOut` — and anything awaited before this line
      // that fails to settle means `onAuthStateChanged` is never registered at
      // all, while the budget timer below still releases the gate. The app then
      // renders signed-out with no listener that could ever correct it: a calm
      // wrong answer produced by the fix for one.
      if (!effectActive) return;

      unsubscribe = auth.onAuthStateChanged((user) => {
        // Bump FIRST, synchronously — this is what lets enrichUserSession
        // (dispatched below for the PREVIOUS callback, possibly still in
        // flight) detect it has been superseded (code-review round 1).
        const generation = ++authGenerationRef.current;
        console.log("Auth state changed:", user ? `User logged in: ${user.email}` : "No user logged in");
        setCurrentUser(user);
        Sentry.setUser(user ? { id: user.uid } : null);

        if (!user) {
          // Reset any prior session's derived state. On a first-attempt sign-out
          // this runs behind the still-held loading gate; on a later sign-out the
          // app is already interactive and this clears it immediately.
          setUserRoles([]);
          setUserProfile(null);
          setAuthState({
            isAuthenticated: false,
            lastTokenRefresh: null,
            tokenExpirationTime: null,
            hasRoleClaim: false,
            tokenValid: false
          });

          // KB-3593 AC-2 (A): the FIRST auth answer of this load is signed-out,
          // yet the load began with a stored user — so the SDK dropped it during
          // boot. Only the first answer: a later sign-out is one the user saw.
          if (generation === 1 && hadPersistedAuthUserAtBoot()) setSessionEndedAtBoot(true);

          // KB-3228: on the FIRST settled sign-out this page session, try to
          // adopt an existing kidsbooked.com apex session as a GATED door before
          // showing the login page — so an allowlisted apex user lands in the app
          // rather than flashing through it.
          //
          // ⚠️ The gate is held only until the FIRST auth resolution, NOT until the
          // access verdict. A successful signInWithCustomToken — allowed OR denied
          // — fires onAuthStateChanged(user) below, which releases the gate before
          // enforceProAccessGate returns. So a DENIED apex user briefly renders the
          // app shell for ~one Firestore read, then the gate's sign-out bounces
          // them to /login (silently — this door's rejection is toast-free). That
          // window is inherent to sign-in-then-gate and identical to the
          // email/google doors; authGenerationRef discards the stale enrichment,
          // Firestore rules gate the data, and the END state is signed out on the
          // login page (AC-2). Only the no-token / fetch-error paths are held at
          // the loading gate until the attempt settles.
          //
          // AT MOST ONCE per mount. A rejection signs the caller back out, which
          // re-fires this callback; re-attempting would loop (fetch → signIn →
          // gate denies → signOut → signed-out → fetch …).
          //
          // KB-3994: that ref alone does NOT protect a sign-out. It is spent only
          // when the page LOADED signed out; on a page that loaded signed in, a
          // sign-out's null answer is the first one here. What does protect it:
          //  - on any page that has been signed in, `ssoDoorOpenRef`, closed by
          //    every signed-in answer. The first signed-out answer still spends
          //    the ref, exactly as before, but does not adopt. That covers this
          //    tab's sign-out and another open tab's, which reaches here through
          //    shared persistence;
          //  - across the reload that `/logout` and `/welcome` do, the tab-scoped
          //    marker in `apexSsoSuppression.ts`, which the federated `signOut`
          //    and `LogoutPage` write, and which any signed-in answer clears.
          // A `{ federated: false }` eject re-opens the door. Because the ref is
          // still spent by whichever signed-out answer comes first, an eject
          // makes an attempt exactly when it did before KB-3994: only as the
          // page's first signed-out answer (KB-3994 AC-6).
          //
          // The 8000ms budget timer above still releases the gate if the attempt
          // hangs (AC-6); deferring here only holds the gate while an attempt is
          // genuinely in flight. A successful adopt releases the gate via the
          // re-entrant user event below (signInWithCustomToken fires
          // onAuthStateChanged), so the `.finally` is an idempotent backstop on
          // that path and the primary release on every other outcome.
          if (!ssoBootstrapAttemptedRef.current) {
            ssoBootstrapAttemptedRef.current = true;
            if (!ssoDoorOpenRef.current) {
              releaseGate('apex-sso-door-closed:page-was-signed-in');
              return;
            }
            if (isApexSsoBootstrapSuppressed()) {
              releaseGate('apex-sso-suppressed:user-signed-out');
              return;
            }
            void attemptApexSsoBootstrap().finally(() =>
              releaseGate('apex-sso-settled'),
            );
            return;
          }

          releaseGate('auth-state-resolved:signed-out');
          return;
        }

        // KB-3425: a session that restores after the budget released the gate
        // got in, so the rescue offered while it could not is withdrawn. A late
        // signed-OUT answer keeps it: that user still has to sign in, through
        // the same SDK that just stalled.
        setShowEscape(false);
        setSessionEndedAtBoot(false);
        // KB-3994: signed in, by any door. This load no longer arrived signed out,
        // so its door closes; the next signed-out LOAD may adopt again.
        ssoDoorOpenRef.current = false;
        clearApexSsoSuppression();

        // Release the gate the moment we have a settled auth state — profile
        // + claims enrichment happens below, off the blocking path (AC3).
        releaseGate('auth-state-resolved:user');
        void enrichUserSession(user, generation);
        // KB-3271 AC-3 (load path): for a RESTORED session (persistence rehydrated
        // with the observed-apex flag still set), check whether the apex session
        // went away while pro was closed. Un-awaited — must never block first
        // render. This is the signed-IN branch, where apexSsoBootstrap does not
        // run, so it adds no second fetch to the bootstrap (signed-out) path.
        void runApexPresenceCheck();
      });
    })();

    return () => {
      effectActive = false;
      clearTimeout(budgetTimer);
      unsubscribe?.();
    };
    // Mount-once. runApexPresenceCheck reads auth state live at call time, so the
    // captured closure is correct; re-running the whole auth listener on its
    // identity would be wrong.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real implementation of login using Firebase Authentication
  //
  // KB-3033: the await is BOUNDED. `signInWithEmailAndPassword` can hang
  // forever rather than reject — @firebase/auth awaits an App Check token
  // before issuing the request, and the reCAPTCHA-v3 provider's deferred has
  // no rejection path and no `script.onerror` (full chain in
  // `src/lib/auth-transport.ts`). Unbounded, that leaves the submit button
  // stuck on "Logging in…" permanently, with no error anywhere.
  const login = async (email: string, password: string): Promise<User> => {
    console.log("Attempting real Firebase login with:", email);
    // KB-3352 AC-3: inside App Check's refusal throttle every mint in this page
    // fails at once, so Auth could only send a dummy token that enforced Identity
    // Toolkit refuses. Do not attempt, and do not make the user wait out
    // AUTH_REQUEST_BUDGET_MS to be told so. Nothing is started, so there is no
    // late settle to reconcile and the KB-3133 gate below is not involved. The
    // read is synchronous and asks the SDK nothing: a refusal is all it needs.
    const preflight = readRefusalEvidence();
    if (preflight.insideRefusalThrottle) {
      const notAttempted = new AuthAttestationRejectedError('signInWithEmailAndPassword');
      Sentry.addBreadcrumb({
        category: 'auth-bootstrap',
        level: 'error',
        message: `login: not attempted — ${preflight.message}`,
        data: preflight.data,
      });
      Sentry.captureException(notAttempted, {
        tags: {
          source: 'AuthContext.login',
          errorType: 'app-check-rejected',
          appcheck_outcome: preflight.outcome,
        },
      });
      console.error("Firebase login not attempted:", notAttempted);
      toast.error(AUTH_ATTESTATION_REJECTED_MESSAGE);
      throw notAttempted;
    }
    // Held in a variable rather than inlined into boundAuthCall: on timeout we
    // need to keep listening to it (see the reconciler below). The race does
    // not cancel it — promises are not cancellable — so without this reference
    // a late success would be invisible to us but perfectly visible to
    // Firebase, which fires onAuthStateChanged regardless.
    //
    // DECLARED here but ASSIGNED inside the try (round-2 O3): a synchronous
    // throw from `signInWithEmailAndPassword` would otherwise escape this
    // function's catch entirely — no toast, no breadcrumb, no capture, and
    // `handleLogin`'s catch only logs, so the user would watch the button reset
    // with nothing on screen.
    let pending: Promise<UserCredential> | undefined;
    try {
      pending = signInWithEmailAndPassword(auth, email, password);
      const userCredential = await boundAuthCall(pending, 'signInWithEmailAndPassword');
      console.log("Login successful for user:", userCredential.user.email);
      // KB-3133: the success toast moved to `LoginPage.handleLogin`, AFTER the
      // allowlist gate. Fired here it announced "Login successful!" to a caller
      // the gate was about to refuse — the reassuring-but-wrong sentence SOP
      // §6a-bis forbids, and one the Google door never showed. It also belongs
      // at the UI boundary by this repo's own convention; see
      // `lib/analytics/eventNames.ts:15-16`, which says the same about the auth
      // events for the same reason.
      return userCredential.user;
    } catch (error) {
      // The timeout is reported separately from a Firebase rejection: it has a
      // different cause and a different remedy, and `handleFirebaseError` would
      // flatten it to "Authentication error: …" (KB-3033 AC-4).
      if (isAuthTransportTimeout(error)) {
        // Round-1 review #1: expiring the race does NOT abort the sign-in. If
        // reCAPTCHA was merely slow, `pending` can still resolve — Firebase
        // then fires onAuthStateChanged, PublicRoute (`:45`) redirects into the
        // app, and DashboardLayout (`:29`) reads a localStorage record that
        // handleLogin's success path never got to write, rendering a blank
        // name and email. Writing it here makes the shell coherent.
        //
        // We do NOT sign a successful user back out (dispatcher ruling): the
        // credential is genuine and discarding it would be a second surprise
        // on top of the first.
        //
        // There is deliberately NO uid guard here, and the first version's was
        // removed rather than repaired (round-2 B2). It read
        // `if (auth.currentUser?.uid !== lateCredential.user.uid) return;` and
        // claimed to stop a superseded attempt clobbering the live session. It
        // could never fire: `_signInWithCredential` awaits
        // `auth._updateCurrentUser(userCredential.user)` (`index-d90d2ee5.js:5875`)
        // BEFORE `return userCredential` (`:5877`), so the SDK has already made
        // this credential the current user by the time we observe it. The
        // condition was made true by the very promise it was guarding against.
        //
        // That is a control that cannot run, and the test certifying it only
        // passed because the mock omitted `_updateCurrentUser`. Worse, had it
        // somehow fired it would have been WRONG — leaving the record showing
        // the previous user while the live session had already switched.
        //
        // The real invariant is simpler and true: whatever credential lands
        // here IS the live session by construction, so writing its record
        // always matches `currentUser`.
        //
        // KB-3133: the reconciler now RUNS THE ALLOWLIST GATE first. Without
        // that this was a documented bypass of the gate `handleLogin` installs:
        // the bound rejects, `handleLogin`'s `await login(...)` throws before it
        // ever reaches the gate, and yet `pending` can still resolve — at which
        // point Firebase fires onAuthStateChanged and `PublicRoute:45` walks the
        // caller into the app having passed no check at all.
        //
        // It gates rather than signing every late settle out, which is what the
        // Google popup arm does. That is not an inconsistency between the two
        // doors; each matches the promise its own timeout copy already makes.
        // `AUTH_POPUP_TIMEOUT_USER_MESSAGE` says the caller has NOT been signed
        // in, so the popup arm must sign out to keep that true.
        // `AUTH_TIMEOUT_USER_MESSAGE` says they WILL be signed in
        // automatically, and for the 25 accounts that actually hold a pro role
        // that stays true. Both are pinned in
        // `tests/unit/auth/errorCopy.distinct.test.ts`.
        //
        // The KB-3033 ruling this preserves — "we do NOT sign a successful user
        // back out; the credential is genuine and discarding it would be a
        // second surprise" — is untouched for the users it was about. A genuine
        // credential that no human authorized for pro is a case that ruling did
        // not consider, and it is the one this ticket exists to close.
        void pending?.then(
          async (lateCredential) => {
            console.warn(
              'Firebase login settled AFTER its bound expired; reconciling the local user record.',
            );
            // ⛔ THE LATE SETTLE ALWAYS GATES. NO SHORT-CIRCUIT. NO EXCEPTIONS.
            //
            // KB-3133 rounds 1–3, and this is the third design here; the first
            // two were both gate bypasses, so read this before adding a
            // condition to it. Any change to this rule needs a dispatcher
            // ruling, not a judgement call.
            //
            // What was tried and why each failed:
            //
            //   1. Skip when a NEWER LOGIN ATTEMPT superseded this one.
            //      Broken. A late settle's credential is ALWAYS already the
            //      live session (`_signInWithCredential` awaits
            //      `_updateCurrentUser` before returning), so "a newer attempt
            //      owns this session" is false exactly when the newer attempt
            //      did NOT end up owning it — e.g. it rejected because the user
            //      typed a bad password on purpose, or it was a different
            //      account whose gated session this one then clobbered.
            //
            //   2. Skip when the gate ALREADY ADMITTED this uid this page
            //      session. Also broken, and worse, because it looked right.
            //      Nothing cleared the memo on sign-out, and the in-app Logout
            //      button (`DashboardLayout.tsx:33`) is
            //      `signOut().then(() => navigate('/login'))` — SPA navigation,
            //      no reload — so the memo outlived the session. A REVOKED
            //      account could re-enter pro with no read and no check.
            //
            // Both were attempts to spare an approved user one unnecessary
            // read. Neither was worth it, and the shape they share is the
            // lesson: any condition that skips this gate is a second source of
            // truth about authorization, with its own lifetime, and every
            // lifetime bug in it is an authorization bug.
            //
            // ⚠️ THE ACCEPTED COST, chosen rather than overlooked (dispatcher
            // ruling, round 3). Because there is no memo, a transient failure of
            // the gate's own read can sign out an approved user who had just
            // retried successfully — they see "we couldn't verify your access"
            // and log in again. That is FAIL-CLOSED, and on this surface it is
            // the right trade against a cache that can admit a revoked role.
            // Guarded because this handler is `void`ed: the second argument
            // below catches a rejection of `pending`, NOT one thrown by this
            // callback, so anything escaping here becomes an unhandled
            // rejection with no user-visible consequence and no report. The
            // pre-KB-3133 body was one synchronous call; awaiting the gate
            // widened the surface enough to be worth closing.
            try {
              const verdict = await enforceProAccessGate(lateCredential.user.uid, 'email');
              if (verdict.outcome !== 'allowed') {
                // Signed out, told, and recorded by the gate. Persisting the
                // record here would leave DashboardLayout rendering a name for
                // a session that no longer exists.
                return;
              }
              persistLoginUserRecord(lateCredential.user);
              // KB-3235: mint the apex `__session` cookie now that the gate has
              // allowed this late-settled credential. Fire-and-forget, after the
              // gate, same as every other door. See `apexSessionMint.ts`.
              void mintApexSession(lateCredential.user);
            } catch (reconcileError) {
              // Fail CLOSED, and round-1 finding 5 is why this line now EXISTS
              // rather than just claiming to. The old body was `console.error`
              // alone, under this same "must not be reconciled into the app
              // shell" comment — but skipping the record write closes nothing:
              // the credential is still the live session, and `PublicRoute:45`
              // walks that caller into the app regardless. A guard that names a
              // property it does not have is worse than no guard, so it now has
              // it.
              //
              // ⚠️ WHAT REACHES THIS CATCH — corrected, round-3 O2. The
              // previous version of this comment said "essentially only a
              // `toast.error` throw lands here" and enumerated why. That
              // enumeration was FALSE, and it was the fourth claim of mine on
              // this ticket to go false under checking, which is why it is
              // spelled out rather than summarised now.
              //
              // Two things reach it:
              //   1. A `toast.error` throw from inside the gate. (The gate
              //      catches its own read failure, and `emitAnalyticsEvent`
              //      cannot throw — `lib/analytics/emitEvent.ts:24-36`, which
              //      wraps BOTH sinks in their own try/catch.)
              //   2. `persistLoginUserRecord` above — a bare
              //      `localStorage.setItem` (`lib/loginUserRecord.ts:24`) with
              //      no internal guard. It throws on `QuotaExceededError` and
              //      in Safari private mode.
              //
              // Case 2 has a consequence this sign-out does not solve: the gate
              // said ALLOWED, so an authorized user is ejected by a storage
              // error, silently — there is no toast on this path, and the last
              // thing on screen is the timeout copy promising they will be
              // signed in automatically. Left as-is deliberately: the fix is
              // behavioural and this ticket's authorized delta is deletion plus
              // corrections. It is recorded on KB-3133 for follow-up, and the
              // dispatcher was told rather than left to find it here.
              console.error(
                'Late-settle allowlist gate failed; signing out the unverified session:',
                reconcileError,
              );
              try {
                await firebaseSignOut(auth);
              } catch (signOutError) {
                console.error('Sign-out after a failed late-settle gate also failed:', signOutError);
              }
            }
          },
          // Already reported to the user via the timeout toast. Swallowing here
          // is not hiding anything: Promise.race already attached handlers, so
          // this adds no unhandled rejection either way.
          () => {},
        );
        // KB-3352: ONE read of the App Check outcome, taken as we stop waiting,
        // feeds the copy, the Sentry tag and the breadcrumb — so what the user
        // is told and what Sentry records cannot describe different states. It
        // is awaited because it asks the SDK whether it still holds a valid
        // token (round-2 B3). The late-settle handler above is already attached,
        // so this read cannot keep that gate from running.
        const attestation = await readAppCheckEvidence();
        Sentry.addBreadcrumb({
          category: 'auth-bootstrap',
          level: 'error',
          message: `login: ${error.awaitName} exceeded ${error.boundMs}ms`,
        });
        Sentry.addBreadcrumb({
          category: 'auth-bootstrap',
          level: 'error',
          message: attestation.message,
          data: attestation.data,
        });
        // AC-5 is only satisfied by a captured EVENT. Breadcrumbs are buffered
        // locally and ship attached to one; `login()` rethrows into
        // handleLogin, which catches, so nothing else in this app would ever
        // capture and the breadcrumb would die with the buffer (round-1 #2).
        // Pattern follows TokenErrorBoundary.tsx:86.
        Sentry.captureException(error, {
          tags: {
            source: 'AuthContext.login',
            errorType: 'auth-transport-timeout',
            appcheck_outcome: attestation.outcome,
          },
        });
        console.error("Firebase login timed out:", error);
        toast.error(authFailureAttestationMessage(attestation.outcome, 'email'));
        throw error;
      }
      // AC-5: the rejection path is breadcrumbed too. `auth/network-request-failed`
      // is the code Nicholas actually hit, and without this a recurrence is
      // only attributable from a user's own console.
      Sentry.addBreadcrumb({
        category: 'auth-bootstrap',
        level: 'error',
        message: `login: signInWithEmailAndPassword rejected with ${
          (error as { code?: string } | null)?.code ?? 'an error carrying no code'
        }`,
      });
      const failureCode = (error as { code?: string } | null)?.code;
      const attestation = await readAppCheckEvidence();
      Sentry.addBreadcrumb({
        category: 'auth-bootstrap',
        level: 'error',
        message: attestation.message,
        data: attestation.data,
      });
      // Round-2 B3: capture only the codes the user cannot cause. A wrong
      // password is not a bug report, and Sentry does no filtering of its own.
      if (failureCode === undefined || SENTRY_CAPTURED_LOGIN_CODES.has(failureCode)) {
        Sentry.captureException(error, {
          tags: {
            source: 'AuthContext.login',
            errorType: failureCode ?? 'unknown',
            appcheck_outcome: attestation.outcome,
          },
        });
      }
      console.error("Firebase login error:", error);
      if (failureCode === APP_CHECK_TOKEN_INVALID_CODE && attestation.outcome === 'rejected') {
        // KB-3352: Identity Toolkit refusing the App Check token is shown as a
        // refusal only inside the SDK's refusal throttle, where retrying here
        // cannot work. Every other token-invalid falls through to the neutral
        // handleFirebaseError copy below, tagged with what the page observed:
        // after a failed mint Auth sent a placeholder (round-1 B2), and while
        // the SDK held a valid token Google had minted it for this session, so
        // its refusal evidences nothing about this browser (round-2 B4).
        toast.error(AUTH_ATTESTATION_REJECTED_MESSAGE);
        throw error;
      }
      const errorMessage = handleFirebaseError(error);
      toast.error(`Login failed: ${errorMessage}`);
      throw error;
    }
  };

  // Real implementation of refreshAuthToken
  const refreshAuthToken = async (): Promise<string | null> => {
    if (!currentUser) return null;
    try {
      console.log("Refreshing auth token for user:", currentUser.email);
      const token = await currentUser.getIdToken(true);
      
      // Update the authentication state after refreshing the token
      const tokenResult = await currentUser.getIdTokenResult();
      const expirationDate = new Date(tokenResult.expirationTime);
      
      setAuthState({
        isAuthenticated: true,
        lastTokenRefresh: new Date(),
        tokenExpirationTime: expirationDate,
        hasRoleClaim: !!tokenResult.claims?.role,
        tokenValid: expirationDate > new Date()
      });
      
      return token;
    } catch (error) {
      console.error("Error refreshing auth token:", error);
      return null;
    }
  };

  // KB-3270: `federated` defaults to TRUE — a bare `signOut()` clears the apex
  // (kidsbooked.com) session cookie as well as the local pro session, because the
  // dangerous failure mode is under-logging-out on a shared device (a surviving
  // consumer-site session the next person inherits). Opting OUT is the deliberate
  // act: the access-refusal / eject paths (`ProtectedRoute.handleGoToLogin`,
  // `CreateAccountPage.handleLoginClick`) pass `{ federated: false }`, because
  // clearing the cookie for someone pro just refused would eject them from a
  // consumer site they are entitled to use (AC-4).
  const signOut = async ({ federated = true }: { federated?: boolean } = {}): Promise<void> => {
    console.log("Signing out user");
    // KB-3271 AC-1: capture the uid BEFORE auth.signOut() nulls currentUser, so the
    // observed-apex flag can be cleared on every sign-out path — the user-initiated
    // federated logout AND the {federated:false} eject paths. This is the chokepoint
    // that clears it, so KB-3270's federated path clears it for free.
    const observedUid = auth.currentUser?.uid;
    try {
      // KB-3994: BEFORE `auth.signOut()`, whose null answer reaches the
      // onAuthStateChanged branch above before this await resumes.
      //  - A user-initiated sign-out writes the marker that carries the sign-out
      //    across a reload, and shuts the apex SSO door. The door is already
      //    shut on any signed-in page unless an eject re-opened it and then
      //    failed to sign out; shutting it here is defence in depth for that
      //    case with web storage unavailable. Open, the door would adopt the
      //    session back on the cookie the revoke below has not cleared yet.
      //  - An eject re-opens it, so it makes the adopt attempt it made before
      //    KB-3994: one, if this is the page's first signed-out answer (AC-6).
      if (federated) {
        ssoDoorOpenRef.current = false;
        suppressApexSsoBootstrap();
      } else {
        ssoDoorOpenRef.current = true;
      }

      // Local sign-out FIRST, unconditionally — it must NOT sit behind the awaited
      // apex revoke. On a flaky shared device the revoke can hang to its 5s abort,
      // and any window where the local pro session is still live while the user has
      // already walked away hands the next person the camp's ADMIN dashboard —
      // strictly worse than the consumer-site leak this ticket exists to close
      // (dispatcher audit, KB-3270). If `auth.signOut()` itself throws we fall to
      // the outer catch and never revoke, which is correct: the user is not logged
      // out at all, so clearing their consumer session while leaving them signed
      // into pro is the worse of the two states.
      await auth.signOut();

      // KB-3271 AC-1: clear the observed-apex flag now that the local session is
      // gone. Unconditional across intents — a subsequent session for a different
      // reason must not inherit this uid's arming.
      if (observedUid) clearApexSessionObserved(observedUid);

      // Then the apex revoke — still awaited (AC-2), so the toast below waits on it
      // and can reflect its real outcome. `revokeApexSession` never throws and is
      // bounded (APEX_SESSION_REVOKE_BUDGET_MS); the inner try/catch is
      // defense-in-depth in case that never-throws contract were ever broken
      // upstream. The outcome is CAPTURED, not discarded, so the success message
      // does not claim a federated logout that did not actually happen.
      let revokeOutcome: ApexRevokeOutcome | undefined;
      if (federated) {
        try {
          revokeOutcome = await revokeApexSession();
        } catch (revokeError) {
          console.warn('Apex session revoke threw (helper should have swallowed):', revokeError);
          revokeOutcome = 'error';
        }
      }

      // AC-5 (dispatcher override of my original ruling): on a federated logout
      // whose apex revoke did NOT succeed, the success message must not assert the
      // half that failed — a confident "logged out" that instructs the reader to
      // stop looking is the blocking shape here, and on a shared device there IS
      // something to do (sign out of kidsbooked.com directly). Say it plainly, calm,
      // one line, no error toast (most failures are transient blips). A non-federated
      // eject, or a confirmed `revoked`, keeps the standard message.
      if (federated && revokeOutcome !== 'revoked') {
        toast.success(
          "Signed out of KidsBooked Pro. We couldn't confirm sign-out on kidsbooked.com — if you're on a shared device, sign out there too.",
        );
      } else {
        toast.success("Logged out successfully");
      }
      // Reset authentication state when signing out
      setAuthState({
        isAuthenticated: false,
        lastTokenRefresh: null,
        tokenExpirationTime: null,
        hasRoleClaim: false,
        tokenValid: false
      });
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error("Error during sign out");
    }
  };

  const value = {
    currentUser,
    loading,
    userRoles,
    isAdmin,
    userProfile,
    refreshAuthToken,
    login,
    signOut,
    authenticationState: authState
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center">
          <WebsiteLogo width={200} className="mb-6" />
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kidsbooked-blue mx-auto mb-3"></div>
          <p className="text-gray-600">Loading your session…</p>
        </div>
      ) : (
        <>
          {/* KB-3425: outside the gate, because the budget release that turns
              this on is the same render that removes the gate. It states what
              was observed — the load overran — and never a cause. */}
          {showEscape && (
            <div role="status" className="w-full border-b bg-background px-4 py-2 text-center text-sm text-gray-600">
              Loading your session took longer than expected.{' '}
              <Link to="/logout" className="text-kidsbooked-blue underline">
                Sign out and start fresh
              </Link>
            </div>
          )}
          {sessionEndedAtBoot && (
            <div role="status" className="w-full border-b bg-background px-4 py-2 text-center text-sm text-gray-600">
              {SESSION_ENDED_NOTICE}
            </div>
          )}
          {children}
        </>
      )}
    </AuthContext.Provider>
  );
};

export default AuthContext;
